/**
 * Formatting numbers and dates for people.
 *
 * Money arrives from the API as a string, on purpose: JSON numbers are IEEE doubles and
 * AED 38,412,905,118.44 does not survive one intact. Everything here parses at the last
 * possible moment, for display only — no arithmetic on money happens in this app.
 */

/**
 * Money, abbreviated the way an executive reads it.
 *
 * A card showing "AED 38,412,905,118.44" is a number nobody can compare at a glance, and
 * the precision is false comfort — the underlying figures move by more than a fil between
 * one refresh and the next. Full precision belongs on the record, not on the headline.
 */
export function formatMoney(value: string | number | null | undefined, locale = 'en'): string {
  if (value === null || value === undefined || value === '') return '—'
  const amount = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(amount)) return '—'

  const scaled =
    Math.abs(amount) >= 1e9
      ? { value: amount / 1e9, suffix: 'bn' }
      : Math.abs(amount) >= 1e6
        ? { value: amount / 1e6, suffix: 'm' }
        : Math.abs(amount) >= 1e3
          ? { value: amount / 1e3, suffix: 'k' }
          : { value: amount, suffix: '' }

  const digits = scaled.suffix === '' ? 0 : 1
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(scaled.value)

  return `AED ${formatted}${scaled.suffix}`
}

/** Whole numbers with the locale's grouping. */
export function formatCount(value: number | null | undefined, locale = 'en'): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat(locale).format(value)
}

/** A percentage to one decimal, or an em dash when there is nothing to say. */
export function formatPercent(
  value: string | number | null | undefined,
  locale = 'en',
): string {
  if (value === null || value === undefined || value === '') return '—'
  const amount = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(amount)) return '—'
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(amount)}%`
}

/**
 * A date, in the reader's calendar.
 *
 * Arabic uses the Gregorian calendar with Arabic numerals here rather than Hijri: the
 * client's contracts, payment certificates and P6 schedules are all Gregorian, and a
 * dashboard that silently converted them would make every date an argument.
 */
export function formatDate(value: string | null | undefined, locale = 'en'): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE' : locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsed)
}

/**
 * One value, rendered for the kind of column it came from.
 *
 * Used by the grid and by the confirm-diff, so a number reads the same in the table as it
 * does in the change someone is about to approve. A diff that formats differently from the
 * grid invites the question of whether it is showing the same field.
 *
 * `translate` is optional and threaded in rather than imported: this module is pure and is
 * unit-tested without an i18n instance. Booleans are the one kind whose rendering is a word,
 * and a word has to be in the reader's language — "Yes" sitting inside an Arabic diff is
 * exactly the kind of small wrongness that makes a government buyer stop trusting the page.
 */
export function formatValue(
  value: unknown,
  kind: string,
  locale = 'en',
  translate?: (key: string) => string,
): string {
  if (value === null || value === undefined || value === '') return '—'

  switch (kind) {
    case 'money':
      return formatMoney(value as string, locale)
    case 'percent':
      return formatPercent(value as string, locale)
    case 'integer':
    case 'number':
      return formatCount(Number(value), locale)
    case 'date':
    case 'datetime':
      return formatDate(String(value), locale)
    case 'boolean': {
      // A word, not a tick: this is read in a diff and on a printed page.
      const key = value ? 'value.yes' : 'value.no'
      return translate ? translate(key) : value ? 'Yes' : 'No'
    }
    default:
      return String(value)
  }
}

/**
 * A period code as a month people say out loud. `2026-08` -> `Aug 2026`.
 *
 * Parsed rather than `new Date('2026-08')`, which is parsed as UTC midnight and renders as
 * July for anyone west of Greenwich. A reporting period is a label, not an instant.
 */
export function formatMonth(code: string, locale = 'en'): string {
  const match = /^(\d{4})-(\d{2})$/.exec(code)
  if (!match) return code
  const year = Number(match[1])
  const month = Number(match[2]) - 1
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(
    new Date(year, month, 1),
  )
}
